import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Re-export type
export type { Dayjs } from 'dayjs';

// Configurar plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

// No frontend, precisamos definir o fuso horário da aplicação (Brasília - GMT-3)
// para que as mensagens apareçam no horário da empresa, não no horário do computador do usuário.
export const APP_TIMEZONE = 'America/Sao_Paulo';
dayjs.tz.setDefault(APP_TIMEZONE);

/**
 * ISO com Z ou com offset (instante UTC/real): usar dayjs(s).tz — converte o instante para o fuso.
 * dayjs.tz(s, zone) com Z está errado (trata números como hora local na zone, não o instante UTC).
 * String sem fuso: interpretar como horário de parede em APP_TIMEZONE (comportamento do backend).
 */
function parseStringToAppTimezone(dateStr: string): dayjs.Dayjs {
  const t = dateStr.trim();
  const hasInstant =
    /Z$/i.test(t) ||
    (t.includes('T') && /[+-]\d{2}(?::?\d{2})?(?::\d{2})?$/.test(t));
  if (hasInstant) {
    return dayjs(t).tz(APP_TIMEZONE);
  }
  return dayjs.tz(t, APP_TIMEZONE);
}

// Função para criar uma data no timezone da aplicação
export const createAppDate = (date?: string | Date | dayjs.Dayjs) => {
  if (date) {
    if (typeof date === 'string') {
      return parseStringToAppTimezone(date);
    }
    return dayjs(date).tz(APP_TIMEZONE);
  }
  return dayjs().tz(APP_TIMEZONE);
};

// Função para obter o timezone da aplicação
export const getAppTimezone = () => {
  return APP_TIMEZONE;
};

// Função para formatar data no timezone da aplicação
export const formatInAppTimezone = (date: string | Date | dayjs.Dayjs, format: string = 'YYYY-MM-DD HH:mm:ss') => {
  return createAppDate(date).format(format);
};

// Função para converter qualquer data para o timezone da aplicação
export const toAppTimezone = (date: string | Date | dayjs.Dayjs) => {
  if (typeof date === 'string') {
    return parseStringToAppTimezone(date);
  }
  return dayjs(date).tz(APP_TIMEZONE);
};

/**
 * Normaliza `sentAt` para a string guardada no estado das mensagens (ChatWeb / ChatMobile).
 * Usa o mesmo parse `dayjs(sentAt)` + `.format()` em toda a app.
 */
export function normalizeSentAtToLocal(sentAt: string): string {
  const date = dayjs(sentAt);
  return date.isValid() ? date.format() : dayjs().format();
}

/**
 * Horário exibido na bolha (HH:mm) no fuso da app — igual ao separador de dia (`toAppTimezone`).
 */
export function formatMessageTime(sentAt: string): string {
  try {
    const messageTime = toAppTimezone(sentAt);
    if (!messageTime.isValid()) {
      return createAppDate().format('HH:mm');
    }
    return messageTime.format('HH:mm');
  } catch {
    return createAppDate().format('HH:mm');
  }
}

export default dayjs;